import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import Container from 'components/v2/container';
import { Popover, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { humanize, titleize } from 'underscore.string';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import classnames from 'classnames';
import EquipmentSearch from 'components/modules/orders/equipments/EquipmentSearch';
import DataList from 'components/ui/DataList';
import DataPoller from 'components/ui/DataPoller';
import EditEquipmentModal from 'components/modules/equipment/EditEquipmentModal';
import AddEquipmentModal from 'components/modules/equipment/AddEquipmentModal';
import Badge from 'components/ui/Badge';
import { withLocaleContext } from 'providers/locale-settings-context';
import BulkEditEquipmentModal from './BulkEditEquipmentModal';

import CargomaticInput from '../../v2/input';
import EquipmentActionCreators from '../../../actions/EquipmentActionCreators';
import EquipmentStore from '../../../stores/EquipmentStore';

import FilterGroup from './FilterGroup';
import Filters from './Filters';
import SessionStore from '../../../stores/SessionStore';
import Mapping from './Mapping';

import EquipmentCSVExporter from './EquipmentCSVExporter';

const DATE_FIELDS = [
  'last_free_date',
  'last_cut_date',
  'earliest_empty_date',
  'earliest_return_date',
];

const getFiltersWithoutDates = (filters) => {
  const dateFields = [];
  DATE_FIELDS.forEach((field) => {
    dateFields.push(`${field}_start_date`);
    dateFields.push(`${field}_end_date`);
  });

  return _.omit(filters, dateFields);
};

class List extends Component {
  constructor(props) {
    super(props);

    const hiddenColumns = SessionStore.getEquipmentUserColumnPreferences()
      .concat(Mapping.columns)
      .filter((c) => c.disabled)
      .map((c) => c.dataField);

    this.state = {
      bulkActionDisabled: true,
      showAddEquipmentModal: false,
      showBulkEditModal: false,
      showEditEquipmentModal: false,
      equipment: [],
      filtersToggled: false,
      filters: {
        equipment: [],
        status: [],
        page: 1,
        per_page: 50,
        paginate: true,
        sort_by: '_id',
        order: 'desc', // Questionable here but server side expects order currently.
      },
      metaData: {
        equipment: {},
      },
      selectedEquipment: [],
      selectedEquipmentIds: [],
      pages: 1,
      currentPage: 1,
      totalEntries: 0,
      hiddenColumns: _.uniq(hiddenColumns),
    };
  }

  componentDidMount() {
    EquipmentStore.addChangeListener(this.handleEquipmentStoreChange);
    this.fetchEquipment();
  }

  componentWillUnmount() {
    EquipmentStore.removeChangeListener(this.handleEquipmentStoreChange);
  }

  getColumnList() {
    const { t } = this.props;
    const hiddenColumns = this.state.hiddenColumns;
    return Mapping.columns
      .filter((c) => !c.disabled && c.dataField !== 'equipment_id')
      .map((column) => ({
        dataField: column.dataField,
        text: titleize(
          t(column.localizationSlug, { count: column?.count || 1 }),
        ),
        hidden: hiddenColumns.includes(column.dataField),
      }));
  }

  filterSelectedEquipment = () => {
    return this.state.equipment.filter((e) =>
      this.state.selectedEquipmentIds.includes(e._id),
    );
  };

  handleToggleFilters = () => {
    this.setState((prevState) => ({
      filtersToggled: !prevState.filtersToggled,
    }));
  };

  mapColumnHeaders = (columns) => {
    const { t } = this.props;
    return columns.map((column) => {
      const mapped = {
        ...column,
        headerText:
          column.dataField === 'shipment'
            ? t(column.localizationSlug, { count: 1 })
            : t(column.localizationSlug),
      };

      if (column.type === 'datetime') {
        mapped.formatExtraData = {
          locale: this.props.localeData?.languageCodeId,
        };
      }
      return mapped;
    });
  };

  handleRemoveEquipment = (id) => () => {
    this.setState((prevState) => {
      const filters = _.cloneDeep(prevState.filters);
      filters.equipment = filters.equipment.filter((c) => c !== id);
      const metaData = _.cloneDeep(prevState.metaData);
      delete metaData.equipment[id];

      return {
        metaData,
        filters,
      };
    });
  };

  handleShowBulkEditModal = () =>
    this.setState({
      showBulkEditModal: true,
      selectedEquipment: this.filterSelectedEquipment(),
    });

  handleHideBulkEditmodal = () =>
    this.setState({
      showBulkEditModal: false,
      bulkActionDisabled: true,
      selectedEquipmentIds: [],
    });

  handleDropdownSelect = (groupId) => (e) => {
    this.setState((prevState) => {
      const filters = _.cloneDeep(prevState.filters);
      filters[groupId] = e.target.value;
      return { filters };
    });
  };

  handleColumnToggle = (dataField) => (checked) => {
    const hidden = !checked;

    this.setState(
      (currentState) => {
        let hiddenColumns = [...currentState.hiddenColumns];

        if (hidden) {
          hiddenColumns.push(dataField);
        } else {
          hiddenColumns = hiddenColumns.filter((c) => c !== dataField);
        }
        return {
          hiddenColumns,
        };
      },
      () =>
        SessionStore.setEquipmentUserColumnPreferences(
          this.state.hiddenColumns,
        ),
    );
  };

  handleEquipmentStoreChange = () => {
    const equipmentPayload = EquipmentStore.getEquipmentPayload();
    this.setState({
      equipment: equipmentPayload.equipment.map((e) => ({
        ...e,
        onClick: () => {
          this.setState({ showEditEquipmentModal: true, selectedEquipment: e });
        },
      })),
      pages: equipmentPayload.pages,
      totalEntries: equipmentPayload.total,
    });
  };

  handleFilterApply = () => {
    this.buildFilters();
    this.fetchEquipment();
  };

  handleFilterChange = (groupId) => (value) => (checked) => {
    this.setState((prevState) => {
      const { filters: currentFilters } = prevState;

      const filters = { ...currentFilters };
      const index = filters[groupId].findIndex((f) => f === value);

      if (index !== -1 && !checked) {
        filters[groupId] = [
          ...filters[groupId].slice(0, index),
          ...filters[groupId].slice(index + 1),
        ];
        return { filters };
      }
      if (index === -1 && checked) {
        filters[groupId].push(value);
        return { filters };
      }
      return null;
    });
  };

  handleDateChange = (groupId) => (date) => {
    this.setState((prevState) => {
      const { filters: currentFilters } = prevState;

      const filters = { ...currentFilters };
      filters[groupId] = date;
      return { filters };
    });
  };

  handleDateSelect = (groupId) => (date) => {
    this.setState((prevState) => {
      const { filters: currentFilters } = prevState;

      const filters = { ...currentFilters };
      filters[groupId] = date;
      return { filters };
    });
  };

  handleEquipmentSelect = (value) => {
    this.setState((prevState) => {
      const filters = _.cloneDeep(prevState.filters);
      const metaData = _.cloneDeep(prevState.metaData);
      const id = value._id;
      filters.equipment = [...filters.equipment, id];

      metaData.equipment[id] = value.equipment_id;

      return {
        metaData,
        filters,
      };
    });
  };

  fetchEquipment = () => {
    EquipmentActionCreators.getAllEquipment({
      populate: [
        { path: 'owner', select: 'company_name _type' },
        { path: 'shipment', select: 'shipment_id' },
        { path: 'current_shipment', select: 'shipment_id' },
        { path: 'order', select: 'order_id order_type status' },
        {
          path: 'chassis_owner',
          select: 'name company_name _type first_name last_name',
        },
      ],
      ...getFiltersWithoutDates(this.state.filters),
    });
  };

  handleSelection = (equipmentToSelect, checked) => {
    this.setState((prevState) => {
      const { equipment, selectedEquipmentIds: currentSelectedEquipmentIds } =
        prevState;

      let selectedEquipmentIds;
      if (checked) {
        selectedEquipmentIds = [
          ...currentSelectedEquipmentIds,
          equipmentToSelect._id,
        ];
      } else {
        const index = currentSelectedEquipmentIds.findIndex(
          (e) => e === equipmentToSelect._id,
        );
        if (index !== -1) {
          selectedEquipmentIds = [
            ...currentSelectedEquipmentIds.slice(0, index),
            ...currentSelectedEquipmentIds.slice(index + 1),
          ];
        } else {
          selectedEquipmentIds = [...currentSelectedEquipmentIds];
        }
      }

      const bulkActionDisabled = selectedEquipmentIds.length === 0;

      const selectedEquipment = equipment.filter((e) =>
        selectedEquipmentIds.includes(e._id),
      );

      return {
        selectedEquipmentIds,
        selectedEquipment,
        bulkActionDisabled,
      };
    });
  };

  handleSelectAll = (checked) => {
    this.setState((prevState) => {
      const { equipment } = prevState;

      let selectedEquipment = [];
      let selectedEquipmentIds = [];

      let bulkActionDisabled = true;
      if (checked) {
        selectedEquipment = [...equipment];
        selectedEquipmentIds = selectedEquipment.map((s) => s._id);
        bulkActionDisabled = true;
      }

      return {
        bulkActionDisabled,
        selectedEquipmentIds,
        selectedEquipment,
      };
    });
  };

  handlePaginationSelect = (page) => {
    this.setState(
      (prevState) => {
        const filters = _.cloneDeep(prevState.filters);

        const pageIndex = filters[page];
        if (_.isEmpty(pageIndex)) {
          filters.page = page;
          filters.paginate = true;
          // filters.push({ id: 'page', value: page });
          // filters.push({id: 'paginate', value: true});
        } else {
          filters.page = page;
          // filters[pageIndex].value = page;
        }

        return { filters, currentPage: page };
      },
      () => this.fetchEquipment(),
    );
  };

  buildFilters() {
    this.setState((prevState) => {
      const { filters } = prevState;
      filters.dates = {};
      DATE_FIELDS.forEach((field) => {
        const start = filters[`${field}_start_date`];
        const end = filters[`${field}_end_date`];

        if (start || end) {
          filters.dates[field] = {};

          if (start) {
            filters.dates[field].start_date = start.toISOString();
          }
          if (end) {
            if (!start) {
              filters.dates[field].start_date = end.toISOString();
            } else {
              filters.dates[field].end_date = end.toISOString();
            }
          }
        }
      });
      return { filters };
    });
  }

  saveEquipment(equipEntity) {
    EquipmentActionCreators.addEquipment(equipEntity);
  }

  updateEquipment(equipment) {
    EquipmentActionCreators.updateEquipment(equipment);
  }

  renderDateFilter(filterGroup) {
    const selected = this.state.filters[filterGroup.id];
    return (
      <div className="block mt-4 mb-4 date-filter">
        <DatePicker
          selected={selected}
          className="w-full form-field"
          placeholderText={filterGroup.title}
          onChange={this.handleDateChange(filterGroup.id)}
          isClearable
          onSelect={this.handleDateSelect(filterGroup.id)}
        />
      </div>
    );
  }

  renderDateRange(filterGroup) {
    const { t } = this.props;
    const { id } = filterGroup;
    return (
      <div>
        {this.renderDateFilter({
          ...filterGroup,
          id: `${id}_start_date`,
          title: t('START_DATE'),
        })}
        {this.renderDateFilter({
          ...filterGroup,
          id: `${id}_end_date`,
          title: t('END_DATE'),
        })}
      </div>
    );
  }

  renderColumnList() {
    return (
      <Popover id="order-columns" bsStyle="popover max-w-md">
        <div className="space-y-2">
          {this.getColumnList().map((col) => {
            const checked = !col.hidden;
            return (
              <CargomaticInput
                id={col.dataField}
                key={col.dataField}
                type="checkbox"
                onChange={this.handleColumnToggle(col.dataField)}
                label={col.text}
                checked={checked}
                applyDefaultMargins={false}
              />
            );
          })}
        </div>
      </Popover>
    );
  }

  renderEquipmentFilter() {
    return (
      <fieldset>
        <div className="mb-4 customer-search">
          <EquipmentSearch onEquipmentSelect={this.handleEquipmentSelect} />

          <div className="my-4 selected">{this.renderSelectedEquipment()}</div>
        </div>
      </fieldset>
    );
  }

  renderSelectedEquipment() {
    return this.state.filters.equipment.map((c) => {
      const label = this.state.metaData.equipment[c];
      if (!label) {
        return (
          <Badge text={c} key={c} onClick={this.handleRemoveEquipment(c)} />
        );
      }
      return (
        <Badge text={label} key={c} onClick={this.handleRemoveEquipment(c)} />
      );
    });
  }

  renderDropdownFilter(filterGroup) {
    const id = filterGroup.id;
    return (
      <select
        value={this.state.filters[id]}
        className="form-select"
        key={id}
        onChange={this.handleDropdownSelect(filterGroup.id)}
      >
        <option value="">Select an option</option>
        {filterGroup.filters.map((filter) => {
          if (typeof filter === 'object') {
            return (
              <option value={filter.value} key={filter.value}>
                {filter.name}
              </option>
            );
          }
          return (
            <option value={filter} key={filter}>
              {humanize(filter)}
            </option>
          );
        })}
      </select>
    );
  }

  renderFilterGroup(filterGroup) {
    if (filterGroup.type === 'date') {
      return this.renderDateFilter(filterGroup);
    }

    if (filterGroup.type === 'equipment') {
      return this.renderEquipmentFilter(filterGroup);
    }

    if (filterGroup.type === 'dropdown') {
      return this.renderDropdownFilter(filterGroup);
    }

    if (filterGroup.type === 'text') {
      return this.renderTextFilter(filterGroup);
    }

    if (filterGroup.type === 'date_range') {
      return this.renderDateRange(filterGroup);
    }

    const renderedFilters = filterGroup.filters.map((filter) =>
      this.renderFilter(filterGroup.id, filter),
    );
    return renderedFilters;
  }

  renderFilter(groupId, filter) {
    const filters = this.state.filters[groupId] || [];
    return (
      <CargomaticInput
        key={filter}
        id={filter}
        type="checkbox"
        checked={filters.includes(filter)}
        onChange={this.handleFilterChange(groupId)(filter)}
        label={this.renderStatusSlug(filter)}
      />
    );
  }

  renderStatusSlug(filter) {
    const { t } = this.props;
    const pluralSlugArr = ['available', 'unavailable'];
    if (pluralSlugArr.includes(filter)) {
      return titleize(t(filter.toUpperCase(), { count: 1 }));
    }
    return titleize(t(filter.toUpperCase()));
  }

  renderFilterGroups() {
    const { t } = this.props;
    return Filters.map((filterGroup) => {
      const title = t(filterGroup.localizedTitle);
      const groupId = filterGroup.id;
      const renderedFilterGroup = this.renderFilterGroup(filterGroup);
      return (
        <FilterGroup title={title} key={groupId}>
          {renderedFilterGroup}
        </FilterGroup>
      );
    });
  }

  render() {
    const { currentPage, filters: { per_page } } = this.state;
    const { t } = this.props;
    let x;
    if (currentPage === 1) {
      x = currentPage;
    } else {
      x = (this.state.currentPage - 1) * per_page + 1;
    }

    let y;

    if (x === 1) {
      y =
        this.state.totalEntries < per_page ? this.state.totalEntries : per_page;
    } else if (this.state.totalEntries > 0) {
      const count = x + 1 + per_page;
      y = count < this.state.totalEntries ? count : this.state.totalEntries;
    } else {
      y = 0;
    }

    const filterButtonClass = classnames({
      button: true,
      'button-circle': true,
      'button-green': this.state.filtersToggled,
      'button-hollow-green': !this.state.filtersToggled,
      'mr-2': true,
      flex: true,
      block: true,
      'items-center': true,
    });

    const mainClassName = classnames({
      'w-full': true,
      'md:w-3/4': this.state.filtersToggled,
    });

    const bulkEditTooltip = (
      <Tooltip id="bulk-edit-tooltip">
        {t('SELECT_MULTIPLE_EQUIPMENT_TIP')}
      </Tooltip>
    );

    return (
      <Container>
        <div className="mt-10">
          <div className="equipment">
            <DataPoller action={() => this.fetchEquipment()} />

            <div className="flex flex-wrap items-center justify-between p-4 bg-white">
              <div className="flex items-center left-section">
                <button
                  className={filterButtonClass}
                  title={t('TOGGLE_FILTERS')}
                  onClick={this.handleToggleFilters}
                >
                  <span className="fa fa-filter" />
                </button>
                <OverlayTrigger
                  overlay={this.renderColumnList()}
                  placement="bottom"
                  trigger="click"
                >
                  <button className="flex items-center button button-hollow-green">
                    <i className="mr-2 fa fa-columns" />
                    <span>{t('COLUMN', { count: 1 })}</span>
                  </button>
                </OverlayTrigger>
              </div>
              <div className="flex right-section">
                <button
                  type="button"
                  title={t('ADD_EQUIPMENT')}
                  className="mr-2 button button-green"
                  onClick={() => this.setState({ showAddEquipmentModal: true })}
                >
                  <i className="mr-2 fa fa-plus" />
                  {titleize(t('EQUIPMENT'))}
                </button>
                <OverlayTrigger placement="top" overlay={bulkEditTooltip}>
                  <div
                    className={classnames({
                      'cursor-pointer': this.state.bulkActionDisabled,
                    })}
                  >
                    <button
                      type="button"
                      disabled={this.state.bulkActionDisabled}
                      className="mr-2 button button-hollow-green"
                      onClick={this.handleShowBulkEditModal}
                    >
                      {t('BULK_EDIT')}
                    </button>
                  </div>
                </OverlayTrigger>
                <div className="mr-2">
                  <EquipmentCSVExporter
                    filters={this.state.filters}
                    columns={this.state.activeColumns}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap equipment-section">
            {this.state.filtersToggled && (
              <div className="w-full filters-sidebar bg-grey-lightest md:w-1/4">
                <div className="p-4">
                  <h4>{t('FILTER', { count: 2 })}</h4>
                  {this.renderFilterGroups()}
                </div>
                <button
                  className="float-right my-4 mr-4 button button-green"
                  onClick={this.handleFilterApply}
                >
                  {titleize(t('APPLY'))}
                </button>
              </div>
            )}
            <div className={mainClassName}>
              <DataList
                showHeaders
                select
                selected={this.state.selectedEquipmentIds}
                hover
                striped
                data={this.state.equipment}
                columns={this.mapColumnHeaders(Mapping.columns)}
                hiddenColumns={this.state.hiddenColumns}
                perPage={this.state.filters.per_page}
                pages={this.state.pages}
                page={this.state.currentPage}
                total={this.state.totalEntries}
                paginate
                onPaginationSelect={(page) => this.handlePaginationSelect(page)}
                onSelect={(selectedEquipment, checked) => {
                  this.handleSelection(selectedEquipment, checked);
                }}
                onSelectAll={(checked) => this.handleSelectAll(checked)}
              />
            </div>
          </div>
          {this.state.showEditEquipmentModal && (
            <EditEquipmentModal
              show={this.state.showEditEquipmentModal}
              onHide={() => this.setState({ showEditEquipmentModal: false })}
              equipment={this.state.selectedEquipment}
              onSave={this.updateEquipment}
            />
          )}

          {this.state.showAddEquipmentModal && (
            <AddEquipmentModal
              show={this.state.showAddEquipmentModal}
              onHide={() => this.setState({ showAddEquipmentModal: false })}
              onSave={this.saveEquipment}
            />
          )}
          {this.state.showBulkEditModal && (
            <BulkEditEquipmentModal
              show={this.state.showBulkEditModal}
              onHide={this.handleHideBulkEditmodal}
              equipments={this.state.selectedEquipment}
            />
          )}
        </div>
      </Container>
    );
  }
}

export default withTranslation()(withLocaleContext(List));
